<template>
  <div v-if="stripeCustomCheckoutSession" class="mb-6">
    <div class="flex items-center">
      <div>
        <div class="text-2xl font-medium text-light" v-html="customRenderRichText(blok.text)" />
        <span v-if="discount" class="bg-white text-black text-xs font-semibold px-2 py-1 rounded-sm mt-2">
          {{ discount.displayName }}
        </span>
      </div>
      <div
        :class="hasDiscount ? '-mt-[29px]' : 'mt-0'"
        class="ml-auto text-right"
      >
        <!-- Trial -->
        <template v-if="isTrial">
          <div class="flex flex-wrap">
            <p class="text-dark-200 mt-2">
              <span class="text-xl">
                <template v-if="trialInfo">
                  {{ trialInfo.trialPeriodDays }} days free, then {{ formatCurrency(trialInfo.amountAfterTrial, stripeCustomCheckoutSession.currency) }} / {{ trialInfo.interval }}
                </template>
              </span>
            </p>
          </div>
        </template>

        <!-- Subscription or One-time payment -->
        <template v-else>
          <span v-if="hasDiscount" class="line-through text-dark-200">{{ regularPrice }}</span>
          <div class="text-xl text-white">
            {{ discountedPrice }}
            <span v-if="isRecurring" class="text-sm"> / {{ interval }}</span>
          </div>
          <div class="text-xs text-dark-200">
            {{ isRecurring ? '(cancel anytime)' : '(one-off payment)' }}
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type Stripe from '@stripe/stripe-js'

const props = defineProps<{
  stripeCustomCheckoutSession: Stripe.StripeCustomCheckoutSession | null
  blok: any
}>()

const discount = computed(() => props.stripeCustomCheckoutSession?.discountAmounts?.[0] ?? null)
const lineItem = computed(() => props.stripeCustomCheckoutSession?.lineItems?.[0] ?? null)
const hasDiscount = computed(() => !!lineItem.value?.discountAmounts?.length)
const isRecurring = computed(() => !!props.stripeCustomCheckoutSession?.recurring)
const isTrial = computed(() => !!props.stripeCustomCheckoutSession?.recurring?.trial)
const interval = computed(() => props.stripeCustomCheckoutSession?.recurring?.interval ?? '')

const regularPrice = computed(() => {
  if (!lineItem.value || !props.stripeCustomCheckoutSession) { return '' }
  return formatCurrency(lineItem.value.amountSubtotal, props.stripeCustomCheckoutSession.currency)
})

const discountedPrice = computed(() => {
  if (!props.stripeCustomCheckoutSession) { return '' }
  return formatCurrency(props.stripeCustomCheckoutSession.total.total, props.stripeCustomCheckoutSession.currency)
})

const trialInfo = computed(() => {
  if (!props.stripeCustomCheckoutSession?.recurring?.trial || !props.stripeCustomCheckoutSession?.recurring?.dueNext) {
    return null
  }
  return {
    trialPeriodDays: props.stripeCustomCheckoutSession.recurring.trial.trialPeriodDays,
    amountAfterTrial: props.stripeCustomCheckoutSession.recurring.dueNext.amountSubtotal - props.stripeCustomCheckoutSession.recurring.dueNext.amountDiscount,
    interval: props.stripeCustomCheckoutSession.recurring.interval,
  }
})

function formatCurrency(value: number, currency: string) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value / 100)
}
</script>
